import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Search } from "lucide-react";
import { api, money, shopDateTime } from "@/lib/api";
import { formatPhone } from "@/lib/phone";
import { openIdRequest } from "@/lib/idRequest";
import { WaitingOnCustomer } from "@/components/WaitingOnCustomer";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";

const FILTERS = [
  ["all", "Everyone"],
  ["flagged", "Over $10,000"],
  ["approaching", "Approaching"],
  ["ids_missing", "ID copies missing"],
];

export default function CashWatch() {
  const { branch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  // A secure link the customer opens on their phone to photograph their ID.
  const requestIds = async (row) => {
    const res = await openIdRequest({
      phone: row.phone,
      customer_name: row.customer_name,
      branch,
    });
    if (res) reload();
  };

  const reload = () => {
    api
      .get("/reports/cash-watch")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not build the cash watch report"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const t = data?.totals;
  const term = q.trim().toLowerCase();
  const rows = (data?.rows || []).filter((r) => {
    if (term
        && !String(r.customer_name || "").toLowerCase().includes(term)
        && !String(r.phone || "").includes(term)) return false;
    if (filter === "flagged") return r.flagged;
    if (filter === "approaching") return !r.flagged;
    if (filter === "ids_missing") return r.ids_required;
    return true;
  });

  return (
    <div className="fade-up" data-testid="cash-watch">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        AML monitoring
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Cash Watch</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Customers at or approaching the $10,000 cash mark — this financial year and all time.
        ID copies are only needed once someone is flagged.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          ["Over $10,000", String(t?.flagged ?? 0), "cash-watch-flagged"],
          ["Approaching", String(t?.approaching ?? 0), "cash-watch-approaching"],
          ["ID copies missing", String(t?.ids_missing ?? 0), "cash-watch-missing"],
        ].map(([label, value, testid]) => (
          <div key={testid} className="border border-border p-5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-2 text-2xl font-bold" data-testid={testid}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <WaitingOnCustomer />

      <div className="mt-6 flex flex-wrap items-center gap-4 border border-border p-4">        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 rounded-none pl-9"
            placeholder="Search by name or phone"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="cash-watch-search"
          />
        </div>
        <div className="flex flex-wrap gap-px bg-border">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`cash-watch-filter-${key}`}
              className={`px-4 py-2 text-sm transition-colors duration-150 ${
                filter === key
                  ? "bg-[#0A0A0A] font-semibold text-white"
                  : "bg-white hover:bg-[#FBF8F2]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-border">
        <table className="w-full min-w-[860px] text-sm" data-testid="cash-watch-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {["Customer", "Phone", "Cash this FY", "Cash all time", "Largest single",
                "KYC", "IDs", "Status", "Ask for ID"].map((h) => (
                <th key={h} className={`px-3 py-2.5 font-semibold ${
                  h.startsWith("Cash") || h === "Largest single" ? "text-right" : "text-left"
                }`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-12 text-center text-muted-foreground">
                  {loading ? "Checking…" : "Nobody near the $10,000 cash mark."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.phone} className="hover:bg-[#FBF8F2]" data-testid={`cash-watch-row-${r.phone}`}>
                <td className="px-3 py-2.5 font-semibold">{r.customer_name || "-"}</td>
                <td className="tabular px-3 py-2.5">{formatPhone(r.phone)}</td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.fy_cash)}</td>
                <td className="tabular px-3 py-2.5 text-right font-bold">{money(r.all_time_cash)}</td>
                <td className="tabular px-3 py-2.5 text-right">{money(r.largest_cash)}</td>
                <td className="px-3 py-2.5 text-xs uppercase tracking-wider">
                  {r.has_kyc ? "on file" : "none"}
                </td>
                <td className="px-3 py-2.5 text-xs uppercase tracking-wider">
                  {r.ids_on_file ? "yes" : "no"}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`px-2 py-1 text-[10px] font-bold uppercase ${
                    r.flagged ? "bg-[#9A3412] text-white" : "bg-[#C5A059] text-[#0A0A0A]"
                  }`} data-testid={`cash-watch-status-${r.phone}`}>
                    {r.flagged ? (r.ids_required ? "ID required" : "Over 10k") : "Approaching"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => requestIds(r)}
                    className="flex items-center gap-1.5 border border-[#1F6F43] px-2.5 py-1.5 text-xs font-semibold text-[#1F6F43] transition-colors duration-150 hover:bg-[#F1FAF4]"
                    data-testid={`cash-watch-id-request-${r.phone}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </button>
                  {r.last_request_at && (
                    <div
                      className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground"
                      data-testid={`cash-watch-last-request-${r.phone}`}
                    >
                      Asked {shopDateTime(r.last_request_at)}
                      {r.last_request_by ? ` · ${r.last_request_by}` : ""}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
